/**
 * Seeded PRNG (mulberry32).
 *
 * Every board in numsLearn is derived from a seed rather than shipped as data:
 * the server sends one 32-bit seed and every player in the session reconstructs
 * the identical board. That is what makes a Race fair, keeps the socket payload
 * tiny, and lets any past round be replayed exactly from `rounds.seed`.
 */
export class Rng {
  private s: number;

  constructor(seed: number) {
    this.s = seed >>> 0;
  }

  /** Uniform float in [0, 1). */
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Uniform integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    return min + Math.floor(this.next() * (max - min + 1));
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Rng.pick: empty array');
    return items[Math.floor(this.next() * items.length)]!;
  }

  /** Fisher-Yates, in place. Returns the same array for chaining. */
  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      const a = items[i]!;
      items[i] = items[j]!;
      items[j] = a;
    }
    return items;
  }
}

/** A fresh, non-reproducible seed — used when the server opens a new round. */
export function randomSeed(): number {
  return (Math.random() * 0xffffffff) >>> 0;
}
